
  import moment from "moment";
  import Worklog from "@/model/Worklog";
  import {
    computed,
    defineComponent,
    onMounted,
    onUnmounted,
    reactive,
    Ref,
    set,
    SetupContext,
    toRefs,
    watch
  } from "vue";
  import {helpers, required} from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";
  import WorklogService from "@/service/WorklogService";
  import WorklogServiceV2 from "@/service/WorklogServiceV2";
  import {MessageTypes} from "@/components/message-types";
  import {WorklogType, WorklogTypes} from "@/enums";
  import CreateWorklogParameters
    from "@/views/Accounting/WorklogDetailsDialog/CreateWorklogParameters";
  import Order from "@/model/Order";
  import WorklogDetailsDialogDateField
    from "@/views/Accounting/WorklogDetailsDialog/WorklogDetailsDialogDateField.vue";
  import WorklogDetailsDialogEmployeeField
    from "@/views/Accounting/WorklogDetailsDialog/WorklogDetailsDialogEmployeeField.vue";
  import WorklogDetailsDialogOrderField
    from "@/views/Accounting/WorklogDetailsDialog/WorklogDetailsDialogOrderField.vue";
  import WorklogDetailsDialogTimeField
    from "@/views/Accounting/WorklogDetailsDialog/WorklogDetailsDialogTimeField.vue";
  import Employee from "@/model/Employee";
  import {AxiosError, AxiosResponse} from "axios";
  import {isAuthorized, t} from "@/mixin/mixins";
  import eventBus from "@/eventbus";
  import store from "@/store/store";
  import OrderServiceV2 from "@/service/OrderServiceV2";
  import {WORKLOG_OVERLAP_PROBLEM_TYPE, WorklogOverlapProblem} from "@/model/WorklogOverlapProblem";

  // The fields that show the server errors reported for their path
  const FIELDS = ["date", "employee", "order", "start", "end", "total", "worklogType", "comment"];
  // The fields of the paths POST /v2/worklogs reports that differ from the field names
  const V2_ERROR_FIELDS: Record<string, string> = {
    employeeId: "employee",
    orderId: "order",
    type: "worklogType",
    endNotBeforeStart: "end"
  };

  export default defineComponent({
    name: "WageDialog",
    components: {
      WorklogDetailsDialogDateField,
      WorklogDetailsDialogEmployeeField,
      WorklogDetailsDialogOrderField,
      WorklogDetailsDialogTimeField
    },
    setup (props, context: SetupContext) {
      const initialState = {
        availableOrders: new Array<Order>(),
        createMode: false,
        dateDisabled: false,
        dialog: false,
        employeeDisabled: false,
        model: new Worklog() as Worklog,
        minDateOrder: "",
        maxDateOrder: "",
        orderDisabled: false,
        serverErrors: new Map<string, string>(),
        // Server errors that belong to no single field, e.g. an overlap with another worklog
        generalErrors: new Array<string>()
      };

      const state = reactive({ ...initialState });

      const errors = computed(() => {
        const errors = new Map<string, string | Ref<string>>();
        if (state.serverErrors.size > 0) {
          return state.serverErrors;
        } else {
          if (v$.value.state.$dirty) {
            const model = v$.value.state.model;
            if (model.employee.$error) {
              errors.set("employee", model.employee.$errors[0].$message);
            }
            if (model.order.$error) {
              errors.set("order", model.order.$errors[0].$message);
            }
            if (model.date.$error) {
              errors.set("date", model.date.$errors[0].$message);
            }
            if (model.start.$error) {
              errors.set("start", model.start.$errors[0].$message);
            }
            if (model.end.$error) {
              errors.set("end", model.end.$errors[0].$message);
            }
            if (model.worklogType.$error) {
              errors.set("worklogType", model.worklogType.$errors[0].$message);
            }
          }
          if (v$.value.total.$dirty) {
            if (v$.value.total.$error) {
              errors.set("start", "Startzeit muss vor Endzeit liegen!");
              errors.set("end", "Startzeit muss vor Endzeit liegen!");
            }
          }
        }

        return errors;
      });

      const clearForm = () => {
        state.model = new Worklog();
        v$.value.$reset();
      };

      const close = () => {
        state.serverErrors = new Map<string, string>();
        state.generalErrors = [];
        clearForm();
        state.dialog = false;
        context.emit("closed");
      };

      const deleteWorklog = () => {
        WorklogService.delete(state.model)
          .then((deleted: boolean) => {
            if (deleted) {
              context.emit("deleted");
              eventBus.$emit(MessageTypes.SNACKBAR_SUCCESS, t("entryDeleted"));
              eventBus.$emit(MessageTypes.WORKLOG_DELETED, state.model);
              close();
            }
          });
      };

      const openDetails = (worklog: Worklog) => {
        state.dialog = true;
        state.createMode = false;
        clearForm();

        WorklogServiceV2.getWorklog(worklog.uuid as string)
          .then(loadedWorklog => {
            state.model = loadedWorklog;
          });
      };

      const openCreate = (createWorklogParameters: CreateWorklogParameters) => {
        state.minDateOrder = earliestValidFrom.value;
        state.maxDateOrder = moment().format("YYYY-MM-DD");

        state.dialog = true;
        state.createMode = true;
        clearForm();

        if (createWorklogParameters.order) {
          prefillCreate(createWorklogParameters, createWorklogParameters.order);
        } else if (createWorklogParameters.orderId) {
          // The date range depends on the order, so the form is filled once it is loaded
          OrderServiceV2.getOrder(createWorklogParameters.orderId)
            .then(response => prefillCreate(createWorklogParameters, Object.assign(new Order(), response.data)));
        } else {
          prefillCreate(createWorklogParameters, null);
        }
      };

      const prefillCreate = (createWorklogParameters: CreateWorklogParameters, order: Order | null) => {
        if (order) {
          state.orderDisabled = true;
          set(state.model, "order", order);

          if (moment(order.validFrom).isAfter(state.minDateOrder)) {
            state.minDateOrder = order.validFrom;
          }

          if (order.validTo) {
            if (moment(order.validTo).isBefore(state.maxDateOrder)) {
              state.maxDateOrder = order.validTo;
            }
          }
        }

        if (createWorklogParameters.employee) {
          state.model.employee = Object.assign(new Employee(), createWorklogParameters.employee);
          state.employeeDisabled = true;
        }

        if (createWorklogParameters.date) {
          if (moment(createWorklogParameters.date).isBefore(state.minDateOrder)
            || moment(createWorklogParameters.date).isAfter(state.maxDateOrder)) {
            set(state.model, "date", "");
          } else {
            set(state.model, "date", createWorklogParameters.date);
          }
        }

        if (moment(state.minDateOrder).isSame(state.maxDateOrder)) {
          state.dateDisabled = true;
          set(state.model, "date", state.minDateOrder);
        }

        if (!state.model.date) {
          set(state.model, "date", state.maxDateOrder);
        }
      };

      const save = () => {
        state.serverErrors = new Map<string, string>();
        state.generalErrors = [];
        v$.value.$reset();
        v$.value.$touch();

        if (!v$.value.$invalid) {
          const updated = !!(state.model.id || state.model.uuid);
          let saved: Promise<AxiosResponse>;
          if (updated) {
            saved = WorklogServiceV2.update(state.model.uuid as string, {
              start: state.model.start.substring(0, 5),
              end: state.model.end.substring(0, 5),
              type: state.model.worklogType as WorklogType,
              comment: state.model.comment ? state.model.comment : null
            });
          } else {
            saved = WorklogServiceV2.create({
              employeeId: state.model.employee?.uuid as string,
              orderId: state.model.order?.uuid as string,
              date: state.model.date,
              start: state.model.start.substring(0, 5),
              end: state.model.end.substring(0, 5),
              type: state.model.worklogType as WorklogType,
              comment: state.model.comment ? state.model.comment : null
            });
          }
          saved
            .then(response => {
              state.serverErrors = response.data;
              // A create via v2 answers 201
              if (response.status === 200 || response.status === 201) {
                eventBus.$emit(MessageTypes.SUCCESS, "Änderungen gespeichert.");
                if (updated) {
                  eventBus.$emit(MessageTypes.WORKLOG_UPDATED, state.model);
                } else {
                  eventBus.$emit(MessageTypes.WORKLOG_CREATED, state.model);
                }
                close();
              }
            })
            .catch((error: AxiosError) => {
              if (error && error.response && error.response.status === 409
                && (error.response.data as WorklogOverlapProblem).type === WORKLOG_OVERLAP_PROBLEM_TYPE) {
                const overlappingWorklog = (error.response.data as WorklogOverlapProblem).overlappingWorklog;
                state.generalErrors = [`Arbeitszeit überlappt mit ${overlappingWorklog.start} - ${overlappingWorklog.end}`
                  + ` ${overlappingWorklog.order.name}`];
              } else if (error && error.response && error.response.status === 422) {
                const errorMap = new Map<string, string>();
                const generalErrors = new Array<string>();
                // @ts-ignore
                error.response.data.errors.forEach((error: ValidationError) => {
                  const field = V2_ERROR_FIELDS[error.path] ?? error.path;
                  if (FIELDS.includes(field)) {
                    errorMap.set(field, error.message);
                  } else {
                    generalErrors.push(error.message);
                  }
                });

                // Needed to trigger reactivity
                state.serverErrors = errorMap;
                state.generalErrors = generalErrors;
              }
            });
        }
      };

      const earliestValidFrom = computed(() => {
        return store.state.general.earliestValidFrom;
      });

      const minDate = computed(() => {
        if (state.minDateOrder) {
          return state.minDateOrder;
        } else {
          return earliestValidFrom.value;
        }
      });

      const maxDate = computed(() => {
        if (state.maxDateOrder) {
          return state.maxDateOrder;
        } else {
          return moment().endOf("month").format("YYYY-MM-DD");
        }
      });

      const isAuthorizedEdit = computed(() => {
        return isAuthorized("Accounting_Overview_EditSelf")
          || isAuthorized("Accounting_Overview_EditOther")
          || isAuthorized("Order_Worklogs_SelfEdit")
          || isAuthorized("Order_Worklogs_OtherEdit");
      });

      const isEditable = computed(() => {
        const isDateBefore = moment(state.model.date).isBefore(moment(earliestValidFrom.value));
        return isAuthorizedEdit.value && !isDateBefore;
      });

      const total = computed(() => {
        if (state.model.start != null && state.model.end != null) {
          const minutes = moment(state.model.end, "HH:mm:ss").diff(moment(state.model.start, "HH:mm:ss"), "minutes");
          if (minutes < 0) {
            return "Ungültig";
          } else {
            return moment.utc().startOf("day").add(minutes, "minutes").format("HH:mm");
          }
        } else {
          return "00:00h";
        }
      });

      const worklogTypes = computed(() => {
        return WorklogTypes;
      });

      const rules = computed(() => ({
        state: {
          model: {
            date: {
              required: helpers.withMessage("Datum ist erforderlich.", required)
            },
            employee: {
              required: helpers.withMessage("Mitarbeiter ist erforderlich.", required)
            },
            order: {
              required: helpers.withMessage("Auftrag ist erforderlich.", required)
            },
            start: {
              required: helpers.withMessage("Start ist erforderlich.", required)
            },
            end: {
              required: helpers.withMessage("Ende ist erforderlich.", required)
            },
            worklogType: {
              required: helpers.withMessage("Arbeitsart ist erforderlich.", required)
            }
          },
        },
        total: {
          startBeforeEnd (): boolean {
            // @ts-ignore
            if (state.model && state.model.start && state.model.end) {
              // @ts-ignore
              const minutes = moment(state.model.end, "HH:mm:ss").diff(moment(state.model.start, "HH:mm:ss"), "minutes");
              return minutes >= 0;
            } else {
              return true;
            }
          }
        }
      }));

      // @ts-ignore
      const v$ = useVuelidate(rules, { state, total });

      const updateWorklogCallback = (worklog: Worklog) => {
        if (!state.dialog) {
          openDetails(worklog);
        }
      };

      const createWorklogCallback = (createWorklogParameters: CreateWorklogParameters) => {
        if (!state.dialog) {
          openCreate(createWorklogParameters);
        }
      };

      onMounted(() => {
        eventBus.$on(MessageTypes.UPDATE_WORKLOG, updateWorklogCallback);
        eventBus.$on(MessageTypes.CREATE_WORKLOG, createWorklogCallback);
      });

      // Only this dialog's own callbacks are removed, so a left page's dialog no longer opens a worklog of another page
      onUnmounted(() => {
        eventBus.$off(MessageTypes.UPDATE_WORKLOG, updateWorklogCallback);
        eventBus.$off(MessageTypes.CREATE_WORKLOG, createWorklogCallback);
      });

      watch(() => state.model.date, (newValue, oldValue) => {
        if (!state.orderDisabled && newValue && newValue !== oldValue && moment(newValue as string, "YYYY-MM-DD", true).isValid()) {
          OrderServiceV2.findAvailableOrdersV3(state.model.date)
            .then(orders => {
              state.availableOrders = orders;
              if (state.model.order) {
                // A worklog loaded via v3 references its order by uuid only
                const order = state.model.order;
                const index = state.availableOrders.findIndex(availableOrder => order.uuid
                  ? availableOrder.uuid === order.uuid
                  : availableOrder.id === order.id);
                if (index === -1) {
                  // Order is no longer available so drop it here
                  set(state.model, "order", null);
                }
              }
              if (state.availableOrders.length === 1 && !state.model.order) {
                set(state.model, "order", state.availableOrders[0]);
              }
            });
        }
      }, { deep: true });

      return {
        ...toRefs(state),
        v$,
        errors,
        isEditable,
        minDate,
        maxDate,
        total,
        worklogTypes,
        close,
        deleteWorklog,
        save
      };
    }
  });
